import { useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Group as GroupIcon,
  EuroSymbolSharp as EuroIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  RemoveRedEyeOutlined as RemoveRedEyeOutlinedIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useTailwindBrand } from '../../../Theme/useTailwindBrand';
import { fullNameChild } from '../../../Utils/Function';
import { ENV_VERIFIED_TRAINER, isBrandActive } from '../../../config/environment.config';
import DialogLayout from '../../../Components/DialogLayout';
import { AppDispatch } from '../../../Redux/store';
import SponsorshipRepository from '../../../Repositories/SponsorshipRepository';
import { getSponsorship } from '../../../Redux/Actions/SponsorshipActions';
import { SHOW_SUCCESS } from '../../../Redux/Reducers/MessageReducers';

// ─── Chip de statut ──────────────────────────────────────────────────────────

const STATUS_CHIP_MAP: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' }> = {
  SEND: { label: 'Envoyé', color: 'warning' },
  REGISTER: { label: 'Inscrit', color: 'info' },
  PROC_AE: { label: 'Proc. AE', color: 'info' },
  PROC_SAP: { label: 'Proc. SAP', color: 'info' },
  OK_PROC_AE: { label: 'Proc. AE ✓', color: 'success' },
  OK_PROC_SAP: { label: 'Proc. SAP ✓', color: 'success' },
  OK_CONDITION: { label: 'Conditions ✓', color: 'success' },
  ACCOUNT_VALID: { label: 'Compte validé', color: 'default' },
  ADD_STUDENT: { label: 'Élève ajouté', color: 'secondary' },
  FIRST_COURSE: { label: 'Cours ajouté', color: 'primary' },
  COMPLET: { label: 'Complet', color: 'success' },
};

const SponsorshipStatusChip = ({ statut }: { statut: string }) => {
  const info = STATUS_CHIP_MAP[statut] ?? { label: statut ?? '-', color: 'default' as const };
  return <Chip label={info.label} size="small" color={info.color} variant="outlined" />;
};

// ─── Contenu de la modale de détail ──────────────────────────────────────────

interface DetailDialogContentProps {
  sponsor: any;
  totalFees: number;
  monthlyFees: number;
  studentsCount: number;
  daysSinceCreation: number;
  daysUntilExpiration: number;
  classes: any;
}

const DetailDialogContent = ({
  sponsor,
  totalFees,
  monthlyFees,
  studentsCount,
  daysSinceCreation,
  daysUntilExpiration,
  classes,
}: DetailDialogContentProps) => (
  <div className="space-y-4 p-4">
    {/* Statut */}
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      <h3 className={`${classes.primaryText} font-semibold text-center text-lg mb-3 ${classes.fontFamily}`}>
        Statut actuel
      </h3>
      <div className="flex items-center justify-center">
        <SponsorshipStatusChip statut={sponsor?.statut} />
      </div>
    </div>

    {/* Statistiques */}
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
      <h3 className={`${classes.primaryText} font-semibold text-lg mb-4 ${classes.fontFamily}`}>
        Statistiques du parrainage
      </h3>
      <div className="grid grid-cols-3 gap-3">
        <div className="flex flex-col items-center p-3 bg-white rounded-md">
          <div className="flex items-center gap-2 mb-1">
            <GroupIcon className="w-5 h-5 text-orange-600" />
            <span className="text-2xl font-bold text-gray-900">{studentsCount}</span>
          </div>
          <p className="text-xs text-gray-500">Élève{studentsCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex flex-col items-center p-3 bg-white rounded-md">
          <div className="flex items-center gap-2 mb-1">
            <EuroIcon className="w-5 h-5 text-green-600" />
            <span className="text-2xl font-bold text-gray-900">{monthlyFees.toFixed(2)} €</span>
          </div>
          <p className="text-xs text-gray-500">Commission du mois</p>
        </div>
        <div className="flex flex-col items-center p-3 bg-white rounded-md">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUpIcon className="w-5 h-5 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">{totalFees.toFixed(2)} €</span>
          </div>
          <p className="text-xs text-gray-500">Commission totale</p>
          {totalFees > 0 && (
            <p className="text-xs text-blue-600 mt-1">
              Moy : {(totalFees / Math.max(daysSinceCreation, 1) * 30).toFixed(2)} €/mois
            </p>
          )}
        </div>
      </div>
    </div>

    {/* Informations du filleul */}
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
      <h3 className={`${classes.primaryText} font-semibold text-lg mb-4 ${classes.fontFamily}`}>
        Informations du filleul
      </h3>
      <div className="grid lg:grid-cols-2 grid-cols-1 gap-4">
        {sponsor?.firstName && sponsor?.lastName && (
          <div className="flex items-center gap-3 p-3 bg-white rounded-md">
            <PersonIcon className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-xs text-gray-500">Nom complet</p>
              <p className="font-semibold text-gray-900">
                {fullNameChild(sponsor.firstName, sponsor.lastName)}
              </p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 p-3 bg-white rounded-md">
          <EmailIcon className="w-5 h-5 text-blue-600" />
          <div>
            <p className="text-xs text-gray-500">Adresse e-mail</p>
            <p className="font-semibold text-gray-900">{sponsor?.mail || 'Non renseigné'}</p>
          </div>
        </div>
        {sponsor?.phone && (
          <div className="flex items-center gap-3 p-3 bg-white rounded-md">
            <InfoIcon className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-xs text-gray-500">Téléphone</p>
              <p className="font-semibold text-gray-900">{sponsor.phone}</p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 p-3 bg-white rounded-md">
          <CalendarIcon className="w-5 h-5 text-green-600" />
          <div>
            <p className="text-xs text-gray-500">Date de parrainage</p>
            <p className="font-semibold text-gray-900">
              {moment(sponsor?.creationDatetime?.date).format('DD MMMM YYYY')}
            </p>
            <p className="text-xs text-gray-500">Il y a {daysSinceCreation} jours</p>
          </div>
        </div>
        {!isBrandActive(ENV_VERIFIED_TRAINER) && (
          <div className="flex items-center gap-3 p-3 bg-white rounded-md">
            <ScheduleIcon className="w-5 h-5 text-orange-600" />
            <div>
              <p className="text-xs text-gray-500">Date d'expiration</p>
              <p className="font-semibold text-gray-900">
                {moment(sponsor?.expiratedDate?.date).format('DD MMMM YYYY')}
              </p>
              <p className={`text-xs ${daysUntilExpiration > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {daysUntilExpiration > 0
                  ? `Expire dans ${daysUntilExpiration} jours`
                  : `Expiré il y a ${Math.abs(daysUntilExpiration)} jours`}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

// ─── Composant principal ──────────────────────────────────────────────────────

interface ChildSponsorshipProps {
  sponsor: any;
  dataDispatch: any;
}

const ChildSponsorship = ({ sponsor, dataDispatch }: ChildSponsorshipProps) => {
  const { classes } = useTailwindBrand();
  const dispatch = useDispatch<AppDispatch>();
  const [openDetail, setOpenDetail] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [load, setLoad] = useState(false);

  const hasFullName = !!(sponsor?.firstName && sponsor?.lastName);
  const displayName = hasFullName
    ? fullNameChild(sponsor.firstName, sponsor.lastName)
    : sponsor?.mail ?? '-';
  const initials = hasFullName
    ? `${sponsor.firstName.charAt(0)}${sponsor.lastName.charAt(0)}`.toUpperCase()
    : (sponsor?.mail?.charAt(0) ?? '?').toUpperCase();

  const totalFees = sponsor?.fees ? parseFloat(sponsor.fees) : 0;
  const monthlyFees = sponsor?.feesMonth ? parseFloat(sponsor.feesMonth) : 0;
  const studentsCount = sponsor?.students ? parseInt(sponsor.students) : 0;
  const daysSinceCreation = moment().diff(moment(sponsor?.creationDatetime?.date), 'days');
  const daysUntilExpiration = moment(sponsor?.expiratedDate?.date).diff(moment(), 'days');

  const handleDelete = async () => {
    setLoad(true);
    try {
      await SponsorshipRepository.deleteSponsorship(sponsor.id);
      dispatch(getSponsorship(dataDispatch));
      dispatch({ type: SHOW_SUCCESS, payload: 'Le filleul a été supprimé avec succès.' });
      setOpenDelete(false);
    } catch (error) {
      console.error('Erreur lors de la suppression du filleul :', error);
    } finally {
      setLoad(false);
    }
  };

  return (
    <>
      <TableRow
        hover
        sx={{ '&:nth-of-type(odd)': { backgroundColor: (theme) => theme.palette.action.hover } }}
      >
        {/* Filleul */}
        <TableCell align="left">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.875rem', fontWeight: 700, boxShadow: 1 }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
                {displayName}
              </Typography>
              {hasFullName && (
                <Typography variant="caption" color="text.secondary">
                  {sponsor?.mail}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>

        {/* Date */}
        <TableCell align="center">
          <Typography variant="body2">
            {moment(sponsor?.creationDatetime?.date).format('DD MMM YYYY')}
          </Typography>
          {!isBrandActive(ENV_VERIFIED_TRAINER) && (
            <Typography
              variant="caption"
              color={daysUntilExpiration > 0 ? 'text.secondary' : 'error.main'}
            >
              {daysUntilExpiration > 0
                ? `Expire dans ${daysUntilExpiration}j`
                : 'Expiré'}
            </Typography>
          )}
        </TableCell>

        {/* Élèves */}
        <TableCell align="center">
          <Typography variant="body2" fontWeight={700}>
            {studentsCount}
          </Typography>
        </TableCell>

        {/* Commission du mois */}
        <TableCell align="center">
          <Typography variant="body1" fontWeight={700} color="success.main">
            {monthlyFees.toFixed(2)} €
          </Typography>
        </TableCell>

        {/* Commission totale */}
        <TableCell align="center">
          <Typography variant="body1" fontWeight={700} color="info.main">
            {totalFees.toFixed(2)} €
          </Typography>
        </TableCell>

        {/* Statut */}
        <TableCell align="center">
          <SponsorshipStatusChip statut={sponsor?.statut} />
        </TableCell>

        {/* Action */}
        <TableCell align="center">
          <Tooltip title="Consulter le détail">
            <IconButton
              size="small"
              color="primary"
              onClick={() => setOpenDetail(true)}
              sx={{ '&:hover': { bgcolor: 'primary.50' } }}
            >
              <RemoveRedEyeOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Modale détail */}
      <DialogLayout
        title="Détails du filleul"
        maxWidth="md"
        text={
          <DetailDialogContent
            sponsor={sponsor}
            totalFees={totalFees}
            monthlyFees={monthlyFees}
            studentsCount={studentsCount}
            daysSinceCreation={daysSinceCreation}
            daysUntilExpiration={daysUntilExpiration}
            classes={classes}
          />
        }
        open={openDetail}
        handleClose={() => setOpenDetail(false)}
        handleSubmit={() => setOpenDetail(false)}
        handleCloseIcon={() => setOpenDetail(false)}
        customDialogActions="hidden"
      />

      {/* Modale suppression (réservée pour usage futur) */}
      <DialogLayout
        title="Supprimer le filleul"
        text={
          <div className="p-4">
            <p className="text-gray-700 mb-4">
              Vous êtes sur le point de supprimer le filleul{' '}
              <strong className={classes.primaryText}>{displayName}</strong>.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">
                ⚠️ Cette action est irréversible.
              </p>
            </div>
          </div>
        }
        valid="Supprimer"
        cancel="Annuler"
        open={openDelete}
        load={load}
        handleClose={() => setOpenDelete(false)}
        handleSubmit={handleDelete}
      />
    </>
  );
};

export default ChildSponsorship;
